import VerticalViewBase from '../common/VerticalViewBase';
import BookingCardBash from './BookingCardBash';
import BookingCardBashv2 from './BookingCardBashv2';

/**
 * Builds per-slot room occupancy entries for a BashZone ordered-resource booking.
 * Each segment occupies one room for its specific time window.
 * The occupied count reflects rooms in use, not persons.
 */
const resolveBashSlotOccupancy = (booking, startDate, hasValidDate, timeFormatter) => {
  const segments = Array.isArray(booking?.orderedResourceIds) ? booking.orderedResourceIds : [];
  if (segments.length === 0 || !hasValidDate) {
    const label = timeFormatter.format(startDate);
    const sortKey = startDate.getHours() * 60 + startDate.getMinutes();
    return [{ label, sortKey, occupied: 1 }];
  }

  // Each segment represents one room occupied during its own time window.
  // Group by start-time label and count simultaneously active rooms.
  const slotMap = new Map();
  for (const seg of segments) {
    const offsetMin = Number(seg?.startOffsetMinutes ?? 0);
    const offset = Number.isFinite(offsetMin) ? offsetMin : 0;
    const segDate = new Date(startDate.getTime() + offset * 60000);
    const label = timeFormatter.format(segDate);
    const sortKey = segDate.getHours() * 60 + segDate.getMinutes();
    const existing = slotMap.get(label);
    if (!existing) {
      slotMap.set(label, { label, sortKey, occupied: 1 });
    } else {
      existing.occupied += 1;
    }
  }

  return Array.from(slotMap.values()).sort((a, b) => a.sortKey - b.sortKey);
};

const createResolveBashOccupancyAtSlot = durationByIdMs => (booking, slotDate) => {
  if (!(slotDate instanceof Date) || Number.isNaN(slotDate.getTime())) {
    return 0;
  }

  const bookingStartRaw =
    booking?.bookingType === 'RegiondoBooking' ? booking?.eventStart : booking?.startTime;
  const bookingStart = new Date(bookingStartRaw);
  if (Number.isNaN(bookingStart.getTime())) {
    return 0;
  }

  const segments = Array.isArray(booking?.orderedResourceIds) ? booking.orderedResourceIds : [];
  if (segments.length === 0) {
    const bookingEnd = new Date(booking?.endTime);
    if (Number.isNaN(bookingEnd.getTime())) {
      return 0;
    }
    return bookingStart <= slotDate && bookingEnd > slotDate ? 1 : 0;
  }

  let occupied = 0;
  for (const seg of segments) {
    const offsetRaw = Number(seg?.startOffsetMinutes ?? 0);
    const offsetMinutes = Number.isFinite(offsetRaw) ? offsetRaw : 0;

    const durationRaw = Number(seg?.durationMinutes);
    const durationFromSegment = Number.isFinite(durationRaw) && durationRaw > 0 ? durationRaw : null;

    const ridRaw = seg?.resourceId?._id || seg?.resourceId || null;
    const durationFromConfigMs = ridRaw ? Number(durationByIdMs?.get?.(String(ridRaw)) || 0) : 0;
    const durationMinutes =
      durationFromSegment ?? (durationFromConfigMs > 0 ? Math.round(durationFromConfigMs / 60000) : null);

    if (!(durationMinutes > 0)) {
      continue;
    }

    const segStart = new Date(bookingStart.getTime() + offsetMinutes * 60000);
    const segEnd = new Date(segStart.getTime() + durationMinutes * 60000);
    if (segStart <= slotDate && segEnd > slotDate) {
      occupied += 1;
    }
  }

  return occupied;
};

function VertBash({
  bookings,
  date,
  slotCapacity,
  updateBooking,
  capacity,
  capacityTimes,
  handleOpenModal,
  orderedResourcesConfig,
}) {
  const durationByIdMs = orderedResourcesConfig?.durationByIdMs || null;
  const openModalNoBorder = bookingId => handleOpenModal?.(bookingId, null);
  const resolveBookingOccupancyAtSlot = createResolveBashOccupancyAtSlot(durationByIdMs);
  const renderBookingCard = (booking, updateBooking) => {
    if (booking.bookingType === 'InternalBooking') {
      return (
        <BookingCardBashv2
          key={booking._id}
          booking={booking}
          handleOpenModal={openModalNoBorder}
          durationByIdMs={durationByIdMs}
          orderedResourcesConfig={orderedResourcesConfig}
        />
      );
    }
    return (
      <BookingCardBash key={booking._id} booking={booking} updateBookingState={updateBooking} />
    );
  };

  return (
    <VerticalViewBase
      bookings={bookings}
      date={date}
      slotCapacity={slotCapacity}
      updateBooking={updateBooking}
      renderBookingCard={renderBookingCard}
      showCapacity={true}
      showPercentage={false}
      capacityData={capacity}
      capacityTimes={capacityTimes}
      resolveBookingSlotOccupancy={resolveBashSlotOccupancy}
      resolveBookingOccupancyAtSlot={resolveBookingOccupancyAtSlot}
    />
  );
}

export default VertBash;
